import React from "react";
import { formatDuration } from "../../utils/formatDuration";
import useStatusColors from "../../hooks/useStatusColors";
import "./LiveActivityCard.css";

const STATUS_CONFIG = {
  ACTIVE: { label: "Active", icon: "▶" },
  OVERDUE: { label: "Overdue", icon: "⚠" },
  SUBMITTED: { label: "Submitted", icon: "✓" },
  IDLE: { label: "Idle", icon: "–" },
  UPCOMING: { label: "Upcoming", icon: "◷" },
  FREE: { label: "Free", icon: "○" },
};

const LIVE_STATUS_TO_ASSIGNMENT_STATUS = {
  ACTIVE: "IN_PROGRESS",
  OVERDUE: "OVERDUE",
  SUBMITTED: "SUBMITTED",
  IDLE: "PENDING",
  UPCOMING: "PENDING",
  FREE: "SKIPPED",
};

export default function LiveActivityCard({ item, onViewProject }) {
  const { getStatusStyle } = useStatusColors();

  const getCardStyle = (liveStatus) => {
    const assignmentStatus =
      LIVE_STATUS_TO_ASSIGNMENT_STATUS[liveStatus] || "PENDING";
    return getStatusStyle(assignmentStatus);
  };

  const statusInfo = STATUS_CONFIG[item.status] || STATUS_CONFIG.FREE;
  const cardStyle = getCardStyle(item.status);
  const assignment = item.activeAssignment;
  const pct = Math.min(assignment?.percentageElapsed || 0, 999);

  return (
    <div
      className="live-activity-card"
      style={{
        borderLeft: `4px solid ${cardStyle.borderColor}`,
        backgroundColor: cardStyle.backgroundColor,
      }}
    >
      <div className="activity-card-header">
        <div className="activity-card-identity">
          <div className="activity-card-name">
            {item.firstName} {item.lastName}
          </div>
          <div className="activity-card-role">{item.role}</div>
        </div>

        <span
          className="activity-status-badge"
          style={{
            color: cardStyle.textColor,
            background: cardStyle.backgroundColor,
            border: `1px solid ${cardStyle.borderColor}`,
          }}
        >
          {statusInfo.icon} {statusInfo.label}
        </span>
      </div>

      {assignment ? (
        <div className="activity-card-body">
          <div className="activity-stage-name">
            {assignment.stageName}
            <span className="activity-project-name">
              {" - "}
              {assignment.projectName}
            </span>
          </div>

          <div className="activity-card-meta">
            {assignment.totalTimeLoggedMinutes > 0 && (
              <span className="meta-item">
                Time {formatDuration(assignment.totalTimeLoggedMinutes)} logged
              </span>
            )}

            {pct > 0 && (
              <span
                className="meta-item"
                style={{
                  color:
                    pct >= 100
                      ? "#DC2626"
                      : pct >= (assignment.slaWarning || 80)
                        ? "#A16207"
                        : "#6B7280",
                }}
              >
                {pct >= 100 ? "Overdue" : "Progress"} {pct}% of allocated time
              </span>
            )}

            {assignment.activeTimerStartedAt && (
              <span className="meta-item" style={{ color: "#15803D" }}>
                Timer running
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="activity-card-body">
          {item.status === "UPCOMING" && item.upcomingCount > 0 ? (
            <p className="activity-empty-msg">
              {item.upcomingCount} task{item.upcomingCount !== 1 ? "s" : ""}{" "}
              starting soon - none active today
            </p>
          ) : (
            <p className="activity-empty-msg">
              No tasks assigned. Available for work.
            </p>
          )}
        </div>
      )}

      <div className="activity-card-footer">
        {assignment ? (
          <button
            className="btn-secondary btn-sm"
            onClick={() => onViewProject(item)}
          >
            View Project ->
          </button>
        ) : (
          item.status === "FREE" && <div></div>
        )}

        <div className="activity-counts">
          {item.overdueCount > 0 && (
            <span className="count-badge count-badge--overdue">
              {item.overdueCount} overdue
            </span>
          )}
          {item.upcomingCount > 0 && (
            <span className="count-badge count-badge--upcoming">
              {item.upcomingCount} pending
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
